import "bootstrap/dist/css/bootstrap.min.css";
import {
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
} from "chart.js";
import { useEffect, useState } from "react";
import { Line } from "react-chartjs-2";
import { useNavigate } from "react-router-dom";
import { defaultAxiosInstance } from "../api/defaultInstance";
import { useAccess } from "../context/AccessContext";
import { useUser } from "../context/UserContext";
import "../css/Dashboard.css";
import { getHomePath } from "../utils/routeAccess";
import Layout from "./Layout";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

function Dashboard() {
  const { userInfo } = useUser();
  const { hasPermission } = useAccess();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("MAU"); // 추가된 부분: Tab 상태 관리
  const [chartData, setChartData] = useState({
    labels: ["1월", "2월", "3월", "4월", "5월", "6월", "7월", "8월", "9월", "10월", "11월", "12월"],
    datasets: [
      {
        label: "MAU",
        data: Array(12).fill(0),
        backgroundColor: "rgba(54, 162, 235, 0.2)",
        borderColor: "rgba(54, 162, 235, 1)",
      },
    ],
  });

  const currentYear = new Date().getFullYear();
  const years = [`${currentYear}년`, `${currentYear - 1}년`];
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [dates, setDates] = useState([]);
  const [selectedDisplayDate, setSelectedDisplayDate] = useState("");
  const [users, setUsers] = useState([]);
  const [page, setPage] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const limit = 1000;

  const [cardData, setCardData] = useState([
    { title: "Total User", value: 0, change: "", img: "📈", color: "primary", topIcon: "👥" },
    { title: "Basic User", value: 0, change: "", img: "📈", color: "warning", topIcon: "📦" },
    { title: "Standard User", value: 0, change: "", img: "📈", color: "danger", topIcon: "⭐" },
    { title: "Premium User", value: 0, change: "", img: "📈", color: "success", topIcon: "👑" },
  ]);

  const fetchDashboardData = async (year) => {
    try {
      const response = await defaultAxiosInstance.get(`/admin/dashboard?year=${year}`);
      const { userCounts, userChanges, mauData } = response.data;

      const mauDataset = Array(12).fill(0);
      mauData.forEach(({ month, userCount }) => {
        mauDataset[month - 1] = userCount;
      });
      setChartData((prev) => ({
        ...prev,
        datasets: [{ ...prev.datasets[0], data: mauDataset }],
      }));

      const updatedCardData = cardData.map((card) => {
        const { title } = card;
        let value = 0;
        let change = "";
        let img = "📈";

        if (title === "Total User") {
          value = userCounts.totalUserCount;
          const changePercent = userChanges.find((u) => u.subscriptionType === "total")?.changePercent || 0;
          change = `${Math.abs(changePercent)}% ${changePercent >= 0 ? "Up" : "Down"} from yesterday`;
          img = changePercent >= 0 ? "📈" : "📉";
        } else if (title === "Basic User") {
          value = userCounts.basicUserCount;
          const changePercent = userChanges.find((u) => u.subscriptionType === "basic")?.changePercent || 0;
          change = `${Math.abs(changePercent)}% ${changePercent >= 0 ? "Up" : "Down"} from yesterday`;
          img = changePercent >= 0 ? "📈" : "📉";
        } else if (title === "Standard User") {
          value = userCounts.standardUserCount;
          const changePercent = userChanges.find((u) => u.subscriptionType === "standard")?.changePercent || 0;
          change = `${Math.abs(changePercent)}% ${changePercent >= 0 ? "Up" : "Down"} from yesterday`;
          img = changePercent >= 0 ? "📈" : "📉";
        } else if (title === "Premium User") {
          value = userCounts.premiumUserCount;
          const changePercent = userChanges.find((u) => u.subscriptionType === "premium")?.changePercent || 0;
          change = `${Math.abs(changePercent)}% ${changePercent >= 0 ? "Up" : "Down"} from yesterday`;
          img = changePercent >= 0 ? "📈" : "📉";
        }

        return { ...card, value, change, img };
      });

      setCardData(updatedCardData);
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    }
  };

  const fetchUserList = async () => {
    const selected = dates.find((d) => d.display === selectedDisplayDate);
    if (!selected) return;
    try {
      const response = await defaultAxiosInstance.get("/admin/users", {
        params: { date: selected.value, page, limit },
      });
      // API 응답 구조: { users: [...], totalUserCount: number }
      const usersData = response.data?.users;
      const safeUsers = Array.isArray(usersData) ? usersData : [];

      setUsers(safeUsers);
      setTotalUsers(Number(response.data?.totalUserCount) || 0);
    } catch (error) {
      console.error("Error fetching user list:", error);
      setUsers([]); // 에러 발생 시 빈 배열로 설정
      setTotalUsers(0);
    }
  };

  useEffect(() => {
    const today = new Date();
    const newDates = Array.from({ length: 7 }).map((_, i) => {
      const date = new Date();
      date.setDate(today.getDate() - i);

      // KST 기준으로 변경 (UTC+9)
      //const kstDate = new Date(date.getTime() + (9 * 60 * 60 * 1000));

      return {
        display: date.toLocaleDateString("ko-KR", { month: "long", day: "numeric", timeZone: "Asia/Seoul" }),
        value: date.toISOString().split("T")[0],
      };
    });
    setDates(newDates);
    setSelectedDisplayDate(newDates[0].display);
  }, []);

  useEffect(() => {
    fetchUserList();
  }, [selectedDisplayDate, dates, page]);

  useEffect(() => {
    // 대시보드 권한이 없으면 권한이 있는 첫 메뉴로 리다이렉트
    if (!hasPermission("dashboard.read")) {
      navigate(getHomePath(hasPermission), { replace: true });
      return;
    }
    fetchDashboardData(selectedYear);
  }, [userInfo, hasPermission, navigate, selectedYear]);

  return (
    <Layout>
      <div className="dashboard-container d-flex">
        <div className="main-content">
          <div className="row mb-4" style={{ flexShrink: 0 }}>
            {cardData.map((card, index) => (
              <div className="col-6 col-md-3" key={index}>
                <div className={`card shadow-sm border-${card.color}`}>
                  <div className="card-body">
                    <div className="card-content">
                      <div className="text-section">
                        <h5>{card.title}</h5>
                        <h2>{card.value}</h2>
                        <div className="bottom-icon">
                          <span className="icon">{card.img}</span>
                          <span className={`text-${card.color} small-text`}>{card.change}</span>
                        </div>
                      </div>
                      <div className="top-icon">{card.topIcon}</div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="card-chart mb-4 shadow-sm p-3" style={{ flexShrink: 0 }}>
            <div className="d-flex justify-content-between align-items-center">
              <h5>월간 사용자 (MAU)</h5>
              <select
                className="form-select"
                style={{ width: "150px" }}
                value={selectedYear}
                onChange={(e) => {
                  const year = parseInt(e.target.value);
                  setSelectedYear(year);
                }}
              >
                {years.map((year, index) => {
                  const yearValue = currentYear - index;
                  return (
                    <option key={index} value={yearValue}>
                      {year}
                    </option>
                  );
                })}
              </select>
            </div>
            <Line
              data={chartData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: { legend: { display: true }, tooltip: { enabled: true } },
              }}
            />
          </div>

          <div
            className="card-membership"
            // style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}
          >
            <div className="flex justify-content-between align-items-center mb-2" style={{ flexShrink: 0 }}>
              <h5>신규 멤버십 회원( {Array.isArray(users) ? users.length : 0}명 )</h5>
              <select
                className="form-select"
                style={{ width: "150px" }}
                value={selectedDisplayDate}
                onChange={(e) => {
                  setSelectedDisplayDate(e.target.value);
                  setPage(1);
                }}
              >
                {dates.map((date, index) => (
                  <option key={index} value={date.display}>
                    {date.display}
                  </option>
                ))}
              </select>
            </div>
            <div className="table-container" style={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
              <table className="table table-bordered">
                <thead>
                  <tr>
                    <th>No.</th>
                    <th>사용자 이름</th>
                    <th>아이디</th>
                    <th>가입일시</th>
                    <th>멤버십</th>
                    <th>구독기간</th>
                    <th>상태</th>
                  </tr>
                </thead>
                <tbody>
                  {Array.isArray(users)
                    ? users.map((user, index) => {
                        const today = new Date();
                        const endDate = new Date(user.subscriptionInfo?.endDate);
                        let status = "-";
                        if (user.subscriptionInfo?.type === 'free') {
                          status = "구독해지";
                        } else if (user.isFreeTrial) {
                          status = "무료체험중";
                        } else if (endDate >= today) {
                          status = "구독중";
                        }
                        return (
                          <tr key={index}>
                            <td>{(page - 1) * limit + index + 1}</td>
                            <td>{user.name}</td>
                            <td>{user.email}</td>
                            <td>{user.regDate}</td>
                            <td>
                              <span
                                className={`badge badge-${user.subscriptionInfo ? user.subscriptionInfo.type.toLowerCase() : "free"}`}
                              >
                                {user.subscriptionInfo ? user.subscriptionInfo.type : "free"}
                              </span>
                            </td>
                            <td>
                              {user.subscriptionInfo?.startDate} ~ {user.subscriptionInfo?.endDate}
                            </td>
                            <td>
                              <span
                                className={`badge badge-${status === "무료체험중" ? "free" : status === "구독중" ? "owner" : status === "구독해지" ? "cancelled" : "stsndard"}`}
                              >
                                {status}
                              </span>
                            </td>
                          </tr>
                        );
                      })
                    : null}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default Dashboard;
